/**
 * Property API Service
 * Kết nối với backend để lấy dữ liệu bất động sản
 */

import axios from 'axios';
import { API_BASE_URL } from '../config/api';

export interface Property {
  id: string;
  title: string;  // Changed from name to title
  name?: string;  // Keep name as optional for backward compatibility
  location: string;  // Added location field
  address: string;
  description: string;
  price: number;
  area: number;
  bedrooms?: number;  // Added bedrooms
  bathrooms?: number; // Added bathrooms
  type: string;
  status: 'pending' | 'rented';
  images: string[];
  features: string[];
  coordinates: {
    lat: number;
    lng: number;
  };
  province_code: string;
  province_name?: string;
  ward_code?: string;
  ward_name?: string;
  street?: string;  // Added street field
  full_address?: string;  // Added full_address field
  room_number?: string;  // 💡 Mã số phòng/căn hộ
  electricity_price?: number | null;
  water_price?: number | null;
  latitude?: number;  // GPS coordinates
  longitude?: number;  // GPS coordinates
  main_image?: string;  // Main image URL from backend
  gallery_images?: string[];  // Gallery images from backend
  video_url?: string;  // Video URL from backend
  additional_details?: {  // Additional information from backend
    distances?: {
      hospital?: string;
      school?: string;
      market?: string;
      supermarket?: string;
    };
    legal?: {
      document?: string;
      disputeStatus?: string;
    };
    environment?: {
      floodStatus?: string;
      airQuality?: string;
    };
  };
  created_at: string;
  updated_at: string;
}

export interface PropertyStats {
  totalProperties: number;
  averagePrice: number;
  priceRange: {
    min: number;
    max: number;
  };
  averageArea: number;
  areaRange: {
    min: number;
    max: number;
  };
  popularTypes: Array<{
    type: string;
    count: number;
    percentage: number;
  }>;
}

export interface PropertyFilters {
  province_code?: string;
  ward_code?: string;
  property_type?: string;
  min_price?: number;
  max_price?: number;
  min_area?: number;
  max_area?: number;
  status?: string;
  owner_id?: number;  // Filter by owner ID (for profile pages)
  sort_by?: string;  // newest, price, area
  limit?: number;
  offset?: number;
}

export interface PropertyAddress {
  province: string;
  province_name?: string;
  ward: string;
  ward_name?: string;
  street: string;
  full_address: string;
}

export interface PropertyLocation {
  type: string;
  detail: string;
  latitude?: number | null;
  longitude?: number | null;
}

export interface PropertyRules {
  list: string[];
  notes: string;
}

export interface PropertyContract {
  min_duration: string;
  type: string;
  utilities_included: string;
  furniture_status: string;
  notes: string;
}

export interface PropertyPrice {
  amount: number;
  commission: number;
  commission_enabled: boolean;
  deposit: string;
  payment_term: string;
  rent_payment_day: number;
  electricity_price?: number | null;
  water_price?: number | null;
}

export interface PropertyAmenities {
  amenities: { [key: string]: number };
}

export interface PropertyMedia {
  main_image?: string | null;
  gallery: string[];
  video?: string | null;
  annotations?: any[];
}

export interface PropertyAdditionalDetails {
  distances: { [key: string]: string };
  legal: { [key: string]: string };
  environment: { [key: string]: string };
}

export interface PropertyCreateData {
  title: string;
  type: string;
  description: string;
  room_number?: string;  // 💡 Mã số phòng/căn hộ
  address: PropertyAddress;
  location: PropertyLocation;
  area?: number;
  bedrooms?: number;
  bathrooms?: number;
  price: PropertyPrice;
  contract: PropertyContract;
  rules: PropertyRules;
  amenities: PropertyAmenities;
  media: PropertyMedia;
  additional_details?: PropertyAdditionalDetails;
  status?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  total?: number;
}

class PropertyApiService {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
  }


  /**
   * Lấy danh sách bất động sản với bộ lọc
   */
  async getProperties(filters: PropertyFilters = {}): Promise<ApiResponse<Property[]> & { total?: number; has_more?: boolean; count?: number; offset?: number; limit?: number }> {
    try {
      console.log('🏠 Fetching properties with filters:', filters);
      
      const response = await axios.get<any>(`${this.baseURL}/properties`, {
        params: filters,
        timeout: 15000,
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      const backendData = response.data;
      const properties = backendData.data || backendData;
      
      console.log(`✅ Loaded ${properties.length} properties (Total: ${backendData.total || 'unknown'})`);
      return {
        success: true,
        data: properties,
        total: backendData.total,
        count: backendData.count,
        offset: backendData.offset,
        limit: backendData.limit,
        has_more: backendData.has_more
      };
    } catch (error: any) {
      console.error('❌ Error fetching properties:', error);
      
      if (error.code === 'ERR_NETWORK') {
        console.error('🔧 Network Error - Backend không khả dụng:');
        console.error('   1. Khởi động backend: python run.py');
        console.error('   2. Hoặc: start.bat (Windows)');
        console.error('   3. Kiểm tra: http://localhost:8000/health');
      }
      
      return { 
        success: false, 
        data: [], 
        message: 'Backend không khả dụng. Vui lòng khởi động backend trước.',
        total: 0,
        has_more: false
      };
    }
  }

  /**
   * Lấy thống kê bất động sản theo khu vực
   */
  async getPropertyStats(
    provinceCode?: string, 
    wardCode?: string, 
    propertyType?: string
  ): Promise<ApiResponse<PropertyStats>> {
    try {
      console.log(`📊 Fetching property stats for province: ${provinceCode}, ward: ${wardCode}, type: ${propertyType}`);
      
      const params: any = {};
      if (provinceCode) params.province_code = provinceCode;
      if (wardCode) params.ward_code = wardCode;
      if (propertyType) params.property_type = propertyType;

      const response = await axios.get<PropertyStats>(`${this.baseURL}/properties/stats`, {
        params,
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      const backendData = response.data;
      const stats = (backendData as any)?.data || backendData;
      
      console.log(`✅ Loaded property stats:`, stats);
      return {
        success: true,
        data: stats,
      };
    } catch (error: any) {
      console.error('❌ Error fetching property stats:', error);
      
      // Return mock data if backend is not available
      const mockStats: PropertyStats = {
        totalProperties: 150,
        averagePrice: 15000000,
        priceRange: { min: 5000000, max: 50000000 },
        averageArea: 80,
        areaRange: { min: 30, max: 200 },
        popularTypes: [
          { type: 'apartment', count: 60, percentage: 40 },
          { type: 'house', count: 45, percentage: 30 },
          { type: 'office', count: 30, percentage: 20 },
          { type: 'retail', count: 15, percentage: 10 }
        ]
      };

      return {
        success: true,
        data: mockStats,
        message: 'Using mock data - Backend not available'
      };
    }
  }

  /**
   * Tìm kiếm bất động sản theo từ khóa
   */
  async searchProperties(query: string, limit: number = 10): Promise<ApiResponse<Property[]>> {
    try {
      console.log(`🔍 Searching properties for: "${query}"`);
      
      const response = await axios.get<Property[]>(`${this.baseURL}/properties/search`, {
        params: { q: query, limit },
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      const backendData = response.data as any;
      const properties = Array.isArray(backendData) ? backendData : (backendData?.data || backendData);
      
      console.log(`✅ Found ${properties.length} properties for "${query}"`);
      return {
        success: true,
        data: properties,
      };
    } catch (error: any) {
      console.error(`❌ Error searching properties for "${query}":`, error);
      
      return { 
        success: false, 
        data: [], 
        message: 'Không thể tìm kiếm bất động sản. Backend không khả dụng.' 
      };
    }
  }

  /**
   * Lấy chi tiết bất động sản theo ID
   */
  async getPropertyById(id: string): Promise<ApiResponse<Property>> {
    try {
      console.log(`🏠 Getting property details for ID: ${id}`);
      
      const response = await axios.get<Property>(`${this.baseURL}/properties/${id}`, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      const backendData = response.data;
      const property = (backendData as any)?.data || backendData;
      
      console.log(`✅ Property details retrieved: ${property.title || property.name}`);
      return {
        success: true,
        data: property,
        message: 'Lấy chi tiết bất động sản thành công'
      };
    } catch (error: any) {
      console.error(`❌ Error getting property details for ${id}:`, error);
      
      if (error.response?.status === 404) {
        return {
          success: false,
          data: {} as Property,
          message: 'Không tìm thấy bất động sản'
        };
      }
      
      return {
        success: false,
        data: {} as Property,
        message: 'Không thể lấy chi tiết bất động sản. Vui lòng thử lại sau.'
      };
    }
  }

  /**
   * Lấy bất động sản gần một vị trí
   */
  async getPropertiesNearLocation(
    lat: number, 
    lng: number, 
    radius: number = 5, // km
    limit: number = 20
  ): Promise<ApiResponse<Property[]>> {
    try {
      console.log(`📍 Fetching properties near location: ${lat}, ${lng} (radius: ${radius}km)`);
      
      const response = await axios.get<Property[]>(`${this.baseURL}/properties/nearby`, {
        params: { lat, lng, radius, limit },
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      const backendData = response.data;
      const properties = (backendData as any)?.data || backendData;
      
      console.log(`✅ Found ${properties.length} properties near location`);
      return {
        success: true,
        data: properties,
      };
    } catch (error: any) {
      console.error(`❌ Error fetching properties near location:`, error);
      
      return { 
        success: false, 
        data: [], 
        message: 'Không thể tải bất động sản gần vị trí này.' 
      };
    }
  }

  /**
   * Tạo bất động sản mới
   */
  async createProperty(propertyData: PropertyCreateData): Promise<ApiResponse<Property>> {
    try {
      console.log('🏠 Creating new property:', propertyData.title);
      
      // Prepare request
      const requestData = {
        property_data: propertyData
      };
      
      const response = await axios.post<Property>(`${this.baseURL}/properties`, requestData, {
        timeout: 15000,
        headers: this.getAuthHeaders(),
      });

      const backendData = response.data;
      const property = (backendData as any)?.data || backendData;
      
      console.log(`✅ Property created successfully: ${property.title}`);
      return {
        success: true,
        data: property,
        message: 'Tạo bất động sản thành công!'
      };
    } catch (error: any) {
      console.error('❌ Error creating property:', error);
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        let errorMessage = '';
        
        // Xử lý lỗi validation từ FastAPI (422)
        if (Array.isArray(detail)) {
          // Format mảng lỗi validation
          errorMessage = detail.map((err: any) => {
            if (typeof err === 'string') return err;
            if (err.msg) {
              const field = err.loc && err.loc.length > 1 ? err.loc.slice(1).join('.') : 'Dữ liệu';
              return `${field}: ${err.msg}`;
            }
            return JSON.stringify(err);
          }).join('\n');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
        
        return {
          success: false,
          data: {} as Property,
          message: errorMessage
        };
      }
      
      if (error.response?.status === 422) {
        return {
          success: false,
          data: {} as Property,
          message: 'Dữ liệu không hợp lệ. Vui lòng kiểm tra lại các trường bắt buộc.'
        };
      }
      
      return {
        success: false,
        data: {} as Property,
        message: error.message || 'Không thể tạo bất động sản. Vui lòng thử lại sau.'
      };
    }
  }

  /**
   * Upload một ảnh
   */
  async uploadImage(file: File): Promise<ApiResponse<{ url: string; filename: string }>> {
    try {
      console.log('📸 Uploading image:', file.name);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await axios.post(`${this.baseURL}/upload-image`, formData, {
        timeout: 30000,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      const result = response.data;
      console.log(`✅ Image uploaded successfully: ${result.data?.filename}`);
      
      return {
        success: true,
        message: result.message || 'Upload ảnh thành công',
        data: result.data
      };
    } catch (error: any) {
      console.error('❌ Error uploading image:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Có lỗi xảy ra khi upload ảnh',
        data: null as any
      };
    }
  }

  /**
   * Upload nhiều ảnh
   */
  async uploadImages(files: File[]): Promise<ApiResponse<Array<{ url: string; filename: string }>>> {
    try {
      console.log('📸 Uploading images:', files.length);
      
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await axios.post(`${this.baseURL}/upload-images`, formData, {
        timeout: 60000,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      const result = response.data;
      console.log(`✅ Images uploaded successfully: ${result.data?.length} files`);
      
      return {
        success: true,
        message: result.message || 'Upload ảnh thành công',
        data: result.data
      };
    } catch (error: any) {
      console.error('❌ Error uploading images:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Có lỗi xảy ra khi upload ảnh',
        data: null as any
      };
    }
  }

  /**
   * Upload video
   */
  async uploadVideo(file: File): Promise<ApiResponse<{ url: string; filename: string }>> {
    try {
      console.log('🎥 Uploading video:', file.name);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await axios.post(`${this.baseURL}/upload-video`, formData, {
        timeout: 300000, // 5 minutes for video
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      const result = response.data;
      console.log(`✅ Video uploaded successfully: ${result.data?.filename}`);
      
      return {
        success: true,
        message: result.message || 'Upload video thành công',
        data: result.data
      };
    } catch (error: any) {
      console.error('❌ Error uploading video:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Có lỗi xảy ra khi upload video',
        data: null as any
      };
    }
  }

  /**
   * Cập nhật bất động sản
   */
  async updateProperty(propertyId: number, propertyData: Partial<PropertyCreateData>): Promise<ApiResponse<Property>> {
    try {
      console.log(`🏠 Updating property ${propertyId}:`, propertyData.title);
      
      const response = await axios.put<Property>(`${this.baseURL}/properties/${propertyId}`, propertyData, {
        timeout: 15000,
        headers: this.getAuthHeaders(),
      });

      const backendData = response.data;
      const property = (backendData as any)?.data || backendData;
      
      console.log(`✅ Property updated successfully: ${property.title}`);
      return {
        success: true,
        data: property,
        message: 'Cập nhật bất động sản thành công!'
      };
    } catch (error: any) {
      console.error(`❌ Error updating property ${propertyId}:`, error);
      
      if (error.response?.data?.detail) {
        return {
          success: false,
          data: {} as Property,
          message: error.response.data.detail
        };
      }
      
      return {
        success: false,
        data: {} as Property,
        message: 'Không thể cập nhật bất động sản. Vui lòng thử lại sau.'
      };
    }
  }

  /**
   * Xóa bất động sản
   */
  async deleteProperty(propertyId: number): Promise<ApiResponse<boolean>> {
    try {
      console.log(`🏠 Deleting property ${propertyId}`);
      
      await axios.delete(`${this.baseURL}/properties/${propertyId}`, {
        timeout: 10000,
        headers: this.getAuthHeaders(),
      });

      console.log(`✅ Property deleted successfully`);
      return {
        success: true,
        data: true,
        message: 'Xóa bất động sản thành công!'
      };
    } catch (error: any) {
      console.error(`❌ Error deleting property ${propertyId}:`, error);
      
      if (error.response?.data?.detail) {
        return {
          success: false,
          data: false,
          message: error.response.data.detail
        };
      }
      
      return {
        success: false,
        data: false,
        message: 'Không thể xóa bất động sản. Vui lòng thử lại sau.'
      };
    }
  }

  /**
   * Get user's rental limit info
   */
  async getRentalLimitInfo(): Promise<ApiResponse<{
    rental_count: number;
    subscription_active: boolean;
    subscription_level: string;
    subscription_end_date: string | null;
    days_remaining?: number;
    limit: number | null;
    can_create: boolean;
    remaining_slots?: number | null;
    plan_label?: string;
    required_plan?: string | null;
    required_plan_label?: string | null;
  }>> {
    try {
      const response = await axios.get(`${this.baseURL}/rental-limit-info`, {
        timeout: 10000,
        headers: this.getAuthHeaders(),
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ Error getting rental limit info:', error);
      
      return {
        success: false,
        data: {
          rental_count: 0,
          subscription_active: false,
          subscription_level: 'free',
          subscription_end_date: null,
          days_remaining: 0,
          limit: 0,
          can_create: false
        },
        message: 'Error getting rental limit info'
      };
    }
  }

  /**
   * Tạo hàng loạt bất động sản
   * Returns array of Property IDs for each created property
   */
  async createBulkProperties(propertiesData: PropertyCreateData[]): Promise<ApiResponse<any>> {
    try {
      console.log(`🏠 Creating ${propertiesData.length} properties in bulk`);
      
      // Prepare request with properties array
      const requestData = {
        properties: propertiesData
      };
      
      const response = await axios.post<any>(`${this.baseURL}/properties/bulk`, requestData, {
        timeout: 60000, // 60 seconds for bulk operation
        headers: this.getAuthHeaders(),
      });

      const backendData = response.data;
      
      console.log(`✅ Bulk property creation completed:`, backendData.summary);
      
      return {
        success: backendData.success,
        data: backendData,
        message: backendData.message || `Tạo thành công ${backendData.summary.successful}/${backendData.summary.total} sản phẩm`
      };
    } catch (error: any) {
      console.error('❌ Error creating bulk properties:', error);
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        let errorMessage = '';
        
        // Handle validation errors from FastAPI (422)
        if (Array.isArray(detail)) {
          errorMessage = detail.map((err: any) => {
            if (typeof err === 'string') return err;
            if (err.msg) {
              const field = err.loc && err.loc.length > 1 ? err.loc.slice(1).join('.') : 'Dữ liệu';
              return `${field}: ${err.msg}`;
            }
            return JSON.stringify(err);
          }).join('\n');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
        
        return {
          success: false,
          data: null as any,
          message: errorMessage
        };
      }
      
      if (error.response?.status === 422) {
        return {
          success: false,
          data: null as any,
          message: 'Dữ liệu không hợp lệ. Vui lòng kiểm tra lại các sản phẩm.'
        };
      }
      
      return {
        success: false,
        data: null as any,
        message: error.message || 'Không thể tạo bất động sản hàng loạt. Vui lòng thử lại sau.'
      };
    }
  }
}

export const propertyApi = new PropertyApiService();